import { memo, useState, useEffect, useCallback } from "react";
import Spinner from "../../components/Spinner.js";
import { getOwners, getAnimalsWithActiveMlkRcrd, endMilkingPeriod } from "../../api/index.js";
import useSelectStyles from "../../components/styles/Select.js";
import { useSnackbar } from "../../utils/index.js";
import Form from "../../components/Form.js";
import { Typography, Box, Button, MenuItem, Select, Chip } from "@mui/material";
import InformPopup from "../../components/popups/InformPopup.js";

const EndMilkingPeriod = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [fetchedOwners, setFetchedOwners] = useState([]);
    const [ownersDropdownValues, setOwnersDropdownValues] = useState([]);
    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const [step, setStep] = useState(1);
    const [selectedOwner, setSelectedOwner] = useState(null);
    const [selectedDate, setSelectedDate] = useState(null);
    const [selectedAll, setSelectedAll] = useState(false);
    const [fetchedAnimals, setFetchedAnimals] = useState([]);
    const [selectedAnimals, setSelectedAnimals] = useState([]);
    const [selectedAnimalsRegCodes, setSelectedAnimalsRegCodes] = useState([]);

    const classes = useSelectStyles();
    const { success, error } = useSnackbar();

    const fetchData = useCallback(async () => {
        setIsLoading(true);
        try {
            const { owners } = await getOwners();
            setFetchedOwners(owners);
            setOwnersDropdownValues(
                owners.map((owner) => ({
                    value: `${owner?.lastName} ${owner?.firstName} - ${owner?.code}`,
                    text: `${owner?.lastName} ${owner?.firstName} - ${owner?.code}`,
                }))
            );
        } catch (err) {
            error(err);
        }
        setIsLoading(false);
    }, [error]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const endMilkPeriodFormContent = [
        {
            customType: "dropdown",
            id: "owner",
            items: ownersDropdownValues,
            placeholder: "Εκτροφή",
            required: true,
        },
        {
            customType: "date-picker",
            id: "endDate",
            type: "desktop",
            label: "Ημ/νία Λήξης γαλακτοπαραγωγής",
            width: "595px",
            background: "third",
            required: true,
        },
        {
            customType: "checkbox",
            id: "unit",
            label: "Για να λήξετε την γαλακτοπαραγωγή ολόκληρης της μονάδας τσεκάρετε αυτό το πεδίο",
        },
        {
            customType: "button",
            id: "submit",
            type: "submit",
            text: "Υποβολή",
        },
    ];

    const handleFormSubmit = (values) => {
        const selOwner = fetchedOwners.find(
            (owner) => `${owner?.lastName} ${owner?.firstName} - ${owner?.code}` === values.owner
        );
        setSelectedOwner(selOwner);
        setSelectedDate(values.endDate);
        setSelectedAll(values.unit);
        setIsPopupOpen(true);
    };

    const handelPopupAccept = async () => {
        if (selectedAll) {
            setIsLoading(true);
            setIsPopupOpen(false);
            const valuesToSend = {
                owner: selectedOwner?._id,
                all: true,
                date: selectedDate
            }
            try {
                const { success: scs, message } = await endMilkingPeriod(valuesToSend);
                if (scs) {
                    success(message);
                } else {
                    error(message);
                }
            } catch (err) {
                error(err);
            }
            setIsPopupOpen(false);
            setIsLoading(false);
        } else {
            setIsLoading(true);
            try {
                const valuesToSend = {
                    ownerId: selectedOwner?._id,
                    date: selectedDate,
                }
                const { success: scs, animals, message } = await getAnimalsWithActiveMlkRcrd(valuesToSend);
                if (scs) {
                    if (animals.length > 0) {
                        setFetchedAnimals(animals);
                        setStep(2);
                    } else {
                        error("Δεν υπάρχει ενεργή γαλακτική περίοδος για κάποιο Ζώο πριν τη συγκεκριμένη ημερομηνία")
                    }
                } else {
                    error(message);
                }
            } catch (err) {
                error(err);
            }
            setIsPopupOpen(false);
            setIsLoading(false);
        }
    };

    const handleAnimalSelect = (event) => {
        const selectedAnimalsIds = []
        for (const regCode of event.target.value) {
            selectedAnimalsIds.push(fetchedAnimals.find((an) => an?.registrationCode === regCode)?._id)
        }
        console.log("Selected Animals ids", selectedAnimalsIds)
        setSelectedAnimals(selectedAnimalsIds);
        setSelectedAnimalsRegCodes(event.target.value)
    };

    const handleSubmitSelectedAnimals = async () => {
        setIsLoading(true);
        const valuesToSend = {
            owner: selectedOwner?._id,
            all: false,
            date: selectedDate,
            animals: selectedAnimals,
        }
        try {
            const { success: scs, message } = await endMilkingPeriod(valuesToSend);
            if (scs) {
                success(message);
            } else {
                error(message)
            }
        } catch (err) {
            error(err)
        }
        setStep(1);
        setIsLoading(false);
        // Process the selected animals as needed
    };

    return (
        <>
            <Spinner open={isLoading} />
            <InformPopup
                isOpen={isPopupOpen}
                title={""}
                onClose={() => {
                    setIsPopupOpen(false);
                    setStep(1);
                }}
                onClick={() => {
                    setIsPopupOpen(false);
                    setStep(1);
                }}
                buttonText={"Ακύρωση"}
                onAccept={() => handelPopupAccept()}
                acceptText={"Εντάξει"}
                innerText={[
                    "Λήγοντας την γαλακτοπαραγωγή δεν θα μπορείτε να εισάγετε πλέον παραγωγικά στοιχεία. Για το λόγο αυτό η λήξη της γαλακτοπαραγωγής πρέπει να γίνεται μόνο όταν έχουν καταγραφεί όλα τα στοιχεία. Θέλετε να συνεχίσετε ;",
                ]}
            />
            {step === 1 && (
                <Box className={classes.outerBox}>
                    <Box container flexDirection="column" className={classes.innerBox}>
                        <Typography variant="h5" sx={{ margin: "1rem" }}>
                            Επιλέξτε την Εκτροφή και την ημερομηνία που σας ενδιαφέρει:
                        </Typography>
                        <Form
                            content={endMilkPeriodFormContent}
                            onSubmit={handleFormSubmit}
                            toResetForm={true}
                        />
                    </Box>
                </Box>
            )}
            {step === 2 && (
                <Box className={classes.outerBox}>
                    <Typography variant="h5" sx={{ margin: "1rem" }}>
                        Επιλέξτε τα ζώα για λήξη γαλακτοπαραγωγής:
                    </Typography>
                    <Select
                        multiple
                        value={selectedAnimalsRegCodes}
                        onChange={handleAnimalSelect}
                        displayEmpty
                        renderValue={(selected) => {
                            if (selected.length === 0) {
                                return <Typography sx={{ color: "#a8a8a8" }}>Επιλέξτε ζώα</Typography>;
                            }
                            return (
                                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, justifyContent: "center", alignItems: "center" }}>
                                    {selected.map((value) => (
                                        <Chip key={value} label={value} />
                                    ))}
                                </Box>
                            );
                        }}
                        sx={{
                            width: "80%",
                            marginBottom: "1rem",
                            border: "1px solid #ccc",
                            borderRadius: "4px",
                            padding: "8px",
                            backgroundColor: "#fff",
                        }}
                    >
                        {fetchedAnimals.map((animal) => (
                            <MenuItem key={animal.registrationCode} value={animal.registrationCode}>
                                {animal.registrationCode}
                            </MenuItem>
                        ))}
                    </Select>
                    <Button
                        variant="contained"
                        sx={{ marginTop: "1rem", backgroundColor: "secondary.main" }}
                        onClick={handleSubmitSelectedAnimals}
                    >
                        Υποβολή
                    </Button>
                </Box>
            )}

        </>
    );
};

export default memo(EndMilkingPeriod);
